import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './App.css';
import Map from './components/Map';
import DataTable from './components/DataTable';
import WebSocketStatus from './components/WebSocketStatus';
import ConnectionBanner from './components/ConnectionBanner';
import Login from './components/Login';
import Registration from './components/Registration';
import CodeVerification from './components/CodeVerification';
import Navigation from './components/Navigation';
import BottomNav from './components/BottomNav';
import SideRail from './components/SideRail';
import HeaderAvatarMenu from './components/HeaderAvatarMenu';
import Account from './components/Account';
import Receivers from './components/Receivers';
import History from './components/History';
import Predictions from './components/Predictions';
import AirportBoard from './components/AirportBoard';
import Messages from './components/Messages';
import Airband from './components/Airband';
import Statistics from './components/Statistics';
import Notams from './components/Notams';
import Settings from './components/Settings';
import Downloads from './components/Downloads';
import Privacy from './components/Privacy';
import Admin from './components/Admin';
import GeospatialConfigurator from './components/GeospatialConfigurator';
import BillingDashboard from './components/BillingDashboard';
import ClaimReceiver from './components/ClaimReceiver';
import StatsOverlay from './components/StatsOverlay';
import PullToRefresh from './components/PullToRefresh';
import useWebSocket from './hooks/useWebSocket';
import useProactiveTokenRefresh from './hooks/useProactiveTokenRefresh';
import { SettingsProvider } from './contexts/SettingsContext';
import { getAccessToken, getUserInfo, saveUserSession, clearUserSession } from './utils/auth';
import { isApiSuccess, getFirstApiError } from './utils/api';
import {
  sendRegistrationRequest,
  sendEmailVerificationRequest,
  getAccountInfo,
  getTrackPoints as getTrackPointsHttp,
  getMaxDistancePoints as getMaxDistancePointsHttp,
  getReceiverSnapshot as getReceiverSnapshotHttp,
  updateProfile as updateProfileHttp,
  addReceiver as addReceiverHttp,
  updateReceiver as updateReceiverHttp,
  deleteReceiver as deleteReceiverHttp,
  getTypeIconMap
} from './utils/httpApi';
import { setRuntimeAircraftSpriteOverrides } from './utils/aircraftSpriteMap';
import { loadAirlineLogoMap, hasCachedAirlineLogoMap } from './utils/airlineLogoMap';
import { forceReload } from './utils/versionCheck';
import { debugLog, debugWarn, debugError } from './utils/debug';
import { isMilitaryAircraft } from './utils/icaoCountryMap';
import { isGroundVehicle } from './utils/groundVehicle';
import { maxRangeKm, mergeRangeData } from './utils/rangeRecords';
import { getAirportSettings, getColourSettings, AIRPORT_DISPLAY_OPTIONS } from './utils/mapStyles';
import { pageFromPath, urlForPage } from './utils/pageUrl';

// /claim/<token> is a standalone landing page from the receiver-onboarding
// magic-link email — render it before the rest of the app boots so the
// user doesn't get an "are you logged in" interstitial in front of the
// device-confirmation flow.
function isClaimRoute() {
  return typeof window !== 'undefined'
    && /^\/claim\//.test(window.location.pathname);
}

// The receiver a user should land on when they have never picked one: their
// earliest owned receiver, else the first receiver shared with them (the
// backend returns shared receivers in the order they were shared).
function firstAddedReceiver(owned, shared) {
  if (owned && owned.length > 0) {
    const addedAt = r => {
      const t = Date.parse(r.createdAt);
      return Number.isNaN(t) ? Infinity : t;
    };
    return [...owned].sort((a, b) => addedAt(a) - addedAt(b))[0];
  }
  return shared[0];
}

function App() {
  const [userEmail, setUserEmail] = useState(null);
  const [authCode, setAuthCode] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [currentPage, setCurrentPage] = useState(() => pageFromPath(window.location.pathname));
  useEffect(() => {
    const next = urlForPage(currentPage, window.location.href);
    if (next !== window.location.href) window.history.replaceState({}, '', next);
  }, [currentPage]);
  // Where the user was before "View Live" took them to the map, so they can get back to the list
  // they were working through. Cleared on any other navigation so it can never become a stale
  // breadcrumb pointing somewhere the user did not come from.
  const [returnToPage, setReturnToPage] = useState(null);
  // Cross-page hand-offs consumed by the target page on mount.
  const [receiversInitialScope, setReceiversInitialScope] = useState(null); // 'mine' | 'all'
  const [downloadsTab, setDownloadsTab] = useState(null);                    // Downloads tab id
  // Cross-page hand-off: when Predictions clicks the hex column, it sets
  // this and switches to History so the History page opens pre-filtered
  // to that airframe. Cleared by History after it consumes it.
  const [historyPreselectHex, setHistoryPreselectHex] = useState(null);
  // The UTC day to open History on. An aircraft that landed yesterday is
  // not in today's list, so pages that know when it was seen pass that time.
  const [historyPreselectDate, setHistoryPreselectDate] = useState(null);
  const jumpToHistory = (hex, seenAt) => {
    // Server times are UTC, usually without a zone suffix; a bare string
    // would be read as local time and can land on the wrong day.
    const s = seenAt ? String(seenAt) : null;
    const t = s ? new Date(/(Z|[+-]\d\d:?\d\d)$/.test(s) ? s : s + 'Z') : null;
    setHistoryPreselectDate(t && !isNaN(t) ? t.toISOString().split('T')[0] : null);
    setHistoryPreselectHex(hex);
    setCurrentPage('history');
  };
  const [profile, setProfile] = useState(null);
  const [receivers, setReceivers] = useState([]);
  const [viewerReceivers, setViewerReceivers] = useState([]);
  const [isViewerOnly, setIsViewerOnly] = useState(false);
  const [airports, setAirports] = useState([]);
  const [tableWidth, setTableWidth] = useState(35); // Table width as percentage
  const [isResizing, setIsResizing] = useState(false);
  const [mapResizeTrigger, setMapResizeTrigger] = useState(0); // Trigger map resize when table width changes
  const [autoZoom, setAutoZoom] = useState(() => {
    // Load from localStorage or default to 'none'
    return localStorage.getItem('autoZoom') || 'none';
  });
  const [showDebugFooter, setShowDebugFooter] = useState(false);
  const [mobileView, setMobileView] = useState('map'); // 'map' or 'table' for mobile tab view
  // Sort of the mobile live table, kept here because the table is unmounted while the map is shown.
  const [liveTableSort, setLiveTableSort] = useState({ key: 'r_dst', direction: 'asc' });
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [aircraftFilter, setAircraftFilter] = useState('all'); // 'all', 'new', 'notRegular', 'military'
  const lastScrollY = useRef(0);
  const scrollThreshold = 10; // Minimum scroll distance to trigger hide/show

  // Toggle fullscreen map mode
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
    // When entering fullscreen, ensure we're on map view
    if (!isFullscreen) {
      setMobileView('map');
    }
  }, [isFullscreen]);

  // Three-way viewport split:
  //   phone   ≤ 600 px : compact header + bottom nav + card lists
  //   tablet  601–1023 : compact header + LEFT RAIL nav (new) + same content
  //   desktop ≥1024 px : full top-bar nav + multi-column tables
  // isMobileLayout stays true for phone+tablet so existing mobile-fork
  // code paths don't change behaviour mid-deploy. isPhoneLayout / isTabletLayout
  // are the new fields used to gate the new rail vs the bottom nav.
  const [isPhoneLayout, setIsPhoneLayout] = useState(false);
  const [isTabletLayout, setIsTabletLayout] = useState(false);
  useEffect(() => {
    const checkLayout = () => {
      const w = window.innerWidth;
      // iPadOS 13+ reports a desktop ("Macintosh") user-agent and an unreliable
      // innerWidth, so width alone misclassifies iPads. Detect iPad-class
      // devices by touch capability, then split by orientation:
      //   • portrait iPad  -> tablet side-rail (narrow; the rail fits well)
      //   • landscape iPad -> desktop layout (top-nav + side-by-side map/table)
      // Orientation comes from matchMedia (reliable even when innerWidth isn't).
      // Real Macs report maxTouchPoints === 0 (never caught); true phones don't
      // match the UA test, so they keep the phone bottom-nav unchanged.
      const isIpad = navigator.maxTouchPoints > 1
        && /Macintosh|iPad/.test(navigator.userAgent || '');
      const isPortrait = window.matchMedia
        ? window.matchMedia('(orientation: portrait)').matches
        : window.innerHeight >= w;
      let phone, tablet;
      if (isIpad) {
        phone = false;             // iPads are never the phone bottom-nav layout
        tablet = isPortrait;       // portrait -> side-rail; landscape -> desktop
      } else {
        phone = w <= 600;
        tablet = w > 600 && w <= 1024;
      }
      setIsPhoneLayout(phone);
      setIsTabletLayout(tablet);
      setIsMobileLayout(phone || tablet);
    };
    checkLayout();
    window.addEventListener('resize', checkLayout);
    window.addEventListener('orientationchange', checkLayout);
    return () => {
      window.removeEventListener('resize', checkLayout);
      window.removeEventListener('orientationchange', checkLayout);
    };
  }, []);

  // Collapsible header on scroll (mobile only, map page only)
  useEffect(() => {
    if (!isMobileLayout || currentPage !== 'map') {
      setHeaderHidden(false);
      return;
    }

    const handleScroll = (e) => {
      const target = e.target;
      // Only handle scroll from cards-container (the mobile list view)
      if (!target.classList?.contains('cards-container')) return;

      const currentScrollY = target.scrollTop;
      const scrollDelta = currentScrollY - lastScrollY.current;

      if (Math.abs(scrollDelta) > scrollThreshold) {
        if (scrollDelta > 0 && currentScrollY > 50) {
          // Scrolling down - hide header
          setHeaderHidden(true);
        } else if (scrollDelta < 0) {
          // Scrolling up - show header
          setHeaderHidden(false);
        }
        lastScrollY.current = currentScrollY;
      }
    };

    // Use capture phase to catch scroll events from nested elements
    document.addEventListener('scroll', handleScroll, true);
    return () => document.removeEventListener('scroll', handleScroll, true);
  }, [isMobileLayout, currentPage]);

  // Receiver selection - load from localStorage
  const [selectedReceiver, setSelectedReceiver] = useState(() => {
    return localStorage.getItem('selectedReceiver') || 'All Receivers';
  });
  const [selectedReceiverUuid, setSelectedReceiverUuid] = useState(() => {
    return localStorage.getItem('selectedReceiverUuid') || 'ALL_RECEIVERS';
  });
  // Whether this browser had a receiver choice before this load. Without one,
  // the first receiver added is selected once the receiver lists arrive.
  const hadStoredReceiverRef = useRef(localStorage.getItem('selectedReceiver') !== null);

  // Combined view used for receiver-name / receiver-uuid lookups: a viewer's
  // visible-receivers (viewerReceivers) plus the user's owned receivers.
  // Most consumers only need to "find a receiver by name to get its lat/lon
  // / uuid" — they don't care whether the user owns it or just has viewer
  // access. Without this combination the find-lookups (map centering,
  // dropdown change, validation effect) silently fall through and viewers
  // see the default Ireland-centered map. Memoised so passing it down as a
  // prop doesn't re-render Map every render.
  const allVisibleReceivers = useMemo(() => {
    const owned = receivers || [];
    const viewable = viewerReceivers || [];
    if (viewable.length === 0) return owned;
    if (owned.length === 0) return viewable;
    // De-duplicate by uuid in case a receiver is somehow in both lists.
    const seen = new Set(owned.map(r => r.receiverUuid));
    return [...owned, ...viewable.filter(r => !seen.has(r.receiverUuid))];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivers, viewerReceivers]);

  // Aircraft selection and track
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [aircraftTrack, setAircraftTrack] = useState(null);
  // Monotonic counter used to discard stale track responses. Each call to
  // handleAircraftSelect increments this; a response only updates state if
  // its captured value still matches when it resolves. Without this, an
  // in-flight fetch from a previous click can land *after* the user has
  // selected a different aircraft (or closed the popup) and overwrite the
  // current state with stale data — symptom: popup briefly shows the old
  // aircraft's photo/details on the new selection.
  const aircraftSelectSeqRef = useRef(0);

  // Initial range outline data from HTTP fetch (before WebSocket updates)
  const [initialFurthestData, setInitialFurthestData] = useState(null);
  const [initialAllTimeFurthestData, setInitialAllTimeFurthestData] = useState(null);

  // Debug: Monitor receivers state changes
  useEffect(() => {
    debugLog('🔄 App.js - receivers state changed:', receivers);
    debugLog('🔄 App.js - receivers length:', receivers.length);
  }, [receivers]);

  // Debug: Monitor airports state changes
  useEffect(() => {
    debugLog('🗺️ App.js - airports state changed:', airports);
    debugLog('🗺️ App.js - airports length:', airports.length);
    if (airports.length > 0) {
      debugLog('🗺️ First airport structure:', airports[0]);
    }
  }, [airports]);

  // Use ref for resizing state to avoid stale closures in event handlers
  const isResizingRef = useRef(false);

  // Handle resize drag - using refs to avoid stale closure issues with frequent re-renders
  const handleMouseDown = useCallback((e) => {
    isResizingRef.current = true;
    setIsResizing(true);
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isResizingRef.current) return;

    const windowWidth = window.innerWidth;
    const mouseX = e.clientX;
    const newTableWidth = ((windowWidth - mouseX) / windowWidth) * 100;

    // Constrain between 20% and 50%
    if (newTableWidth >= 20 && newTableWidth <= 50) {
      setTableWidth(newTableWidth);
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    if (isResizingRef.current) {
      // Trigger map resize when resize ends
      setMapResizeTrigger(prev => prev + 1);
    }
    isResizingRef.current = false;
    setIsResizing(false);
  }, []);

  // Handle resize drag (touch events for mobile/tablet)
  const handleTouchStart = useCallback((e) => {
    isResizingRef.current = true;
    setIsResizing(true);
    e.preventDefault();
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isResizingRef.current) return;
    if (!e.touches || e.touches.length === 0) return;

    const windowWidth = window.innerWidth;
    const touchX = e.touches[0].clientX;
    const newTableWidth = ((windowWidth - touchX) / windowWidth) * 100;

    // Constrain between 20% and 50%
    if (newTableWidth >= 20 && newTableWidth <= 50) {
      setTableWidth(newTableWidth);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (isResizingRef.current) {
      // Trigger map resize when resize ends
      setMapResizeTrigger(prev => prev + 1);
    }
    isResizingRef.current = false;
    setIsResizing(false);
  }, []);

  // Add/remove mouse and touch event listeners for resize
  useEffect(() => {
    // Always attach listeners to document to catch events outside the divider
    const onMouseMove = (e) => handleMouseMove(e);
    const onMouseUp = () => handleMouseUp();
    const onTouchMove = (e) => handleTouchMove(e);
    const onTouchEnd = () => handleTouchEnd();

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchEnd);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Update cursor style when resizing
  useEffect(() => {
    if (isResizing) {
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }, [isResizing]);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      // Airline logos come from the backend (public endpoint, no token). Kick
      // the load off first so it overlaps the token check; a warm start already
      // has the map from localStorage and never waits on it.
      const logoLoad = loadAirlineLogoMap();

      const token = getAccessToken();
      const savedUserInfo = getUserInfo();

      if (token && savedUserInfo) {
        // Token exists - try to use it
        // WebSocket will validate it by trying getAccountInfo
        debugLog('🔐 Found existing JWT token, will validate via WebSocket');
        setIsAuthenticated(true);
        setUserInfo(savedUserInfo);

        // Fetch admin-assigned aircraft type-icon overrides so DB
        // assignments take effect without a FE redeploy. Best-effort —
        // a 4xx/5xx leaves the static sprite map intact.
        getTypeIconMap(token)
          .then(resp => {
            if (resp && resp.icons) {
              const overrides = {};
              Object.keys(resp.icons).forEach(k => {
                const v = resp.icons[k];
                if (v) overrides[k] = { row: v.spriteRow, col: v.spriteCol };
              });
              setRuntimeAircraftSpriteOverrides(overrides);
            }
          })
          .catch(err => debugWarn('Failed to fetch type-icon overrides:', err));
      } else {
        debugLog('⚠️ No existing token found, showing login');
      }

      // Cold start only: hold the "Connecting…" gate for up to 2.5 s so the
      // first paint has logos rather than text codes. Never blocks longer —
      // a slow or dead backend must not stall the app.
      if (!hasCachedAirlineLogoMap()) {
        await Promise.race([logoLoad, new Promise(resolve => setTimeout(resolve, 2500))]);
      }

      // Done checking auth
      setIsCheckingAuth(false);
    };

    checkAuth();
  }, []);

  // Handle authentication and registration responses from backend
  const handleAuthResponse = useCallback((response) => {
    debugLog('Auth/Registration response:', response);

    // Extract service name from header or root level
    const service = response.service || response.header?.service;
    const func = response.function || response.header?.function;

    if (service === 'UserAccountService') {
      if (func === 'getAccountInfo') {
        // GET_ACCOUNT_INFO response - contains profile and receivers data
        // Response structure: { header: { apiStatus: "OK" }, userId: ..., receivers: [...], ... }
        debugLog('📥 getAccountInfo response received');
        debugLog('🔍 Full response structure:', JSON.stringify(response, null, 2));
        debugLog('📋 Response keys:', Object.keys(response));
        debugLog('📋 Header:', response.header);

        // Check API status in header
        const apiStatus = response.header?.apiStatus;

        if (apiStatus === 'OK') {
          debugLog('✅ Account info API status OK');

          // Data is at root level in response (not nested in data object)
          debugLog('🔍 DEBUG accountStatus value:', response.accountStatus);
          debugLog('🔍 DEBUG accountStatus type:', typeof response.accountStatus);

          const profileData = {
            userId: response.userId,
            username: response.username,
            email: response.email,
            name: response.name,
            accountStatus: response.accountStatus,
            roles: response.roles || [],
            active: response.accountStatus === 'ACTIVE', // Map accountStatus to active boolean
            timezone: response.timezone,
            language: response.language,
            emailVerified: response.emailVerified,
            createdAt: response.createdAt,
            lastLogin: response.lastLogin
          };

          debugLog('🔍 DEBUG active field set to:', profileData.active);

          // Extract receivers data (at root level)
          const receiversData = response.receivers || [];
          const viewerReceiversData = response.viewerReceivers || [];
          const isViewerOnlyFlag = response.isViewerOnly || false;

          debugLog('📊 Extracted profile data:', profileData);
          debugLog('📡 Extracted receivers data:', receiversData);
          debugLog('🔢 Number of receivers:', receiversData.length);
          debugLog('🔍 First receiver details:', receiversData[0]);
          debugLog('👁️ Viewer receivers:', viewerReceiversData.length);
          debugLog('👁️ Is viewer only:', isViewerOnlyFlag);

          // Update profile state
          setProfile(profileData);
          debugLog('✓ Profile state updated');

          // Update receivers state
          debugLog('⏳ Setting receivers state with', receiversData.length, 'receivers');
          debugLog('🔍 Receivers data to be set:', receiversData);
          setReceivers(receiversData);
          setViewerReceivers(viewerReceiversData);
          setIsViewerOnly(isViewerOnlyFlag);
          debugLog('✓ Receivers state set - verifying...');

          // Verify the state was updated (will show in next render)
          setTimeout(() => {
            debugLog('🔍 Timeout check - receivers data:', receiversData);
          }, 100);

          // Update userInfo with latest data and save to localStorage
          if (profileData.name || profileData.username) {
            const updatedUserInfo = {
              name: profileData.name || profileData.username,
              email: profileData.email,
              userId: profileData.userId,
              roles: profileData.roles || []
            };
            setUserInfo(updatedUserInfo);

            // Update localStorage with latest info
            const token = getAccessToken();
            const refreshToken = localStorage.getItem('aviotrack_refresh_token');
            const expiryTime = localStorage.getItem('aviotrack_token_expiry');
            if (token && refreshToken) {
              debugLog('💾 Updating localStorage with account info');
              saveUserSession(token, refreshToken, updatedUserInfo, expiryTime);
            }

            debugLog('✓ UserInfo state updated:', updatedUserInfo);
          }
        } else {
          debugError('❌ Failed to get account info - API status:', apiStatus);
          debugError('Full error response:', response);
          debugError('Errors:', response.header?.errors || response.errors);
        }
      }
    } else if (service === 'AuthenticationService') {
      if (func === 'authorized') {
        // AUTHORIZED response - contains full profile and receivers data
        if (isApiSuccess(response)) {
          debugLog('✅ AUTHORIZED MESSAGE RECEIVED');
          debugLog('Full response:', response);

          const { profile: profileData, receivers: receiversData, viewerReceivers: viewerReceiversData, isViewerOnly: isViewerOnlyFlag } = response.data;

          debugLog('📊 Profile data received:', profileData);
          debugLog('📡 Receivers data received:', receiversData);
          debugLog('Number of receivers:', receiversData?.length || 0);
          debugLog('👁️ Viewer receivers:', viewerReceiversData?.length || 0);
          debugLog('👁️ Is viewer only:', isViewerOnlyFlag);

          // Update profile state
          setProfile(profileData);
          debugLog('✓ Profile state updated');

          // Update receivers state
          setReceivers(receiversData || []);
          setViewerReceivers(viewerReceiversData || []);
          setIsViewerOnly(isViewerOnlyFlag || false);
          debugLog('✓ Receivers state updated with', receiversData?.length || 0, 'receivers');
          debugLog('📡 Receiver data details:', receiversData);

          // Update userInfo if not already set
          if (!userInfo && profileData) {
            setUserInfo({
              name: profileData.name,
              email: profileData.email,
              userId: profileData.userId,
              roles: profileData.roles || []
            });
            debugLog('✓ UserInfo state updated');
          }
        } else {
          debugError('❌ AUTHORIZED message failed:', response);
        }
      } else if (func === 'login') {
        // LOGIN response
        if (isApiSuccess(response)) {
          debugLog('✓ Login code sent to email');
          // Check for new format response
          if (response.codeSent) {
            debugLog('Code sent confirmation:', response.message);
          }
        } else {
          const error = getFirstApiError(response) || response.message;
          alert(error || 'Failed to send login code');
          setUserEmail(null);
          setAuthCode(null);
        }
      } else if (func === 'authorise') {
        // AUTHORISE response
        if (isApiSuccess(response)) {
          // New format: fields at root level
          // Legacy format: fields in response.data
          const accessToken = response.accessToken || response.data?.accessToken;
          const refreshToken = response.refreshToken || response.data?.refreshToken;
          const name = response.name || response.data?.name;
          const email = response.email || response.data?.email;
          const userId = response.userId || response.data?.userId;
          const username = response.username || response.data?.username;

          debugLog('✓ Authentication successful:', { username, email, userId });

          saveUserSession(accessToken, refreshToken, { name: name || username, email, userId });
          setIsAuthenticated(true);
          setUserInfo({ name: name || username, email, userId });
          // Clear the auth code after successful authentication
          // This ensures reconnections use JWT token, not the one-time code
          debugLog('✓ Clearing one-time code for future reconnections');
          setAuthCode(null);
        } else {
          const error = getFirstApiError(response) || response.message;
          alert(error || 'Failed to authorize. Please try again.');
          setAuthCode(null);
        }
      } else if (func === 'reconnect') {
        // RECONNECT response - re-authentication with JWT
        if (isApiSuccess(response)) {
          debugLog('✓ Successfully reconnected with JWT token');

          // New format: fields at root level
          // Legacy format: fields in response.data
          const accessToken = response.accessToken || response.data?.accessToken;
          const refreshToken = response.refreshToken || response.data?.refreshToken;
          const name = response.name || response.data?.name;
          const email = response.email || response.data?.email;
          const userId = response.userId || response.data?.userId;
          const username = response.username || response.data?.username;

          // Update tokens in case they were refreshed
          saveUserSession(accessToken, refreshToken, { name: name || username, email, userId });
          setIsAuthenticated(true);
          setUserInfo({ name: name || username, email, userId });
          setIsCheckingAuth(false);
          // Ensure authCode is cleared on reconnection
          setAuthCode(null);
        } else {
          debugLog('❌ JWT token expired or invalid, clearing session');
          // Token expired - clear session and show login
          clearUserSession();
          setIsAuthenticated(false);
          setUserInfo(null);
          setUserEmail(null);
          setAuthCode(null);
          setIsCheckingAuth(false);
        }
      }
    } else if (service === 'RegistrationService') {
      if (func === 'register') {
        // REGISTER response
        if (isApiSuccess(response)) {
          debugLog('Registration successful, verification code sent');
          // Keep registration data for verification step
        } else {
          const error = getFirstApiError(response);
          alert(error || 'Failed to register. Please try again.');
          setRegistrationData(null);
        }
      } else if (func === 'verifyEmail') {
        // VERIFY_EMAIL response
        if (isApiSuccess(response)) {
          const { accessToken, refreshToken } = response.data;
          const { email, name } = registrationData;
          // Assume userId will be in response
          const userId = response.data.userId || null;

          saveUserSession(accessToken, refreshToken, { name, email, userId });
          setIsAuthenticated(true);
          setUserInfo({ name, email, userId });
          setRegistrationData(null);
        } else {
          const error = getFirstApiError(response);
          alert(error || 'Invalid verification code. Please try again.');
        }
      } else if (func === 'updateProfile') {
        // UPDATE_PROFILE response
        if (isApiSuccess(response)) {
          debugLog('Profile updated successfully');
          const updatedData = response.data;

          // Update profile state
          setProfile(updatedData);

          // Update userInfo
          setUserInfo({
            name: updatedData.name,
            email: updatedData.email,
            userId: updatedData.userId
          });

          // Show success message
          if (updatedData.emailVerificationRequired) {
            alert('Profile updated. Email verification required for the new email address.');
          } else {
            alert(response.data.message || 'Profile updated successfully');
          }
        } else {
          const error = getFirstApiError(response);
          alert(error || 'Failed to update profile. Please try again.');
        }
      }
    } else if (service === 'ReceiverService') {
      if (func === 'addReceiver') {
        // ADD_RECEIVER response
        if (isApiSuccess(response)) {
          debugLog('Receiver added successfully');
          const newReceiver = response.data;

          // Add to receivers list
          setReceivers(prev => [...prev, newReceiver]);

          alert(response.data.message || 'Receiver added successfully');
        } else {
          const errors = response.errors || [];
          const errorMessages = errors.map(e => e.message).join('\n');
          alert(errorMessages || 'Failed to add receiver. Please try again.');
        }
      } else if (func === 'updateReceiver') {
        // UPDATE_RECEIVER response
        if (isApiSuccess(response)) {
          debugLog('Receiver updated successfully');
          const updatedReceiver = response.data;

          // Update receiver in list
          setReceivers(prev => prev.map(r =>
            r.receiverUuid === updatedReceiver.receiverUuid ? { ...r, ...updatedReceiver } : r
          ));

          alert(response.data.message || 'Receiver updated successfully');
        } else {
          const errors = response.errors || [];
          const errorMessages = errors.map(e => e.message).join('\n');
          alert(errorMessages || 'Failed to update receiver. Please try again.');
        }
      }
    } else if (service === 'AirportService') {
      if (func === 'getAirportsInBounds') {
        // GET_AIRPORTS_IN_BOUNDS response
        debugLog('✈️ Airport response received:', response);

        if (response.header?.apiStatus === 'OK' && response.airports) {
          const allAirports = response.airports || [];
          debugLog('✈️ Total airports received:', allAirports.length);

          if (allAirports.length === 0) {
            return;
          }

          // Get airport display settings and filter
          const airportSettings = getAirportSettings();
          const displayOption = AIRPORT_DISPLAY_OPTIONS[airportSettings.display];

          if (!displayOption || displayOption.types.length === 0) {
            debugLog('✈️ Airport display disabled');
            setAirports([]);
            return;
          }

          const allowedTypes = displayOption.types;
          const airportsToDisplay = allAirports.filter(airport =>
            allowedTypes.includes(airport.type)
          );

          debugLog('✈️ Filtered airports:', airportsToDisplay.length, '(display:', airportSettings.display, ')');
          setAirports(airportsToDisplay);
        } else {
          debugError('❌ Airport condition FAILED');
        }
      }
    }
  }, [registrationData, setIsCheckingAuth, userInfo]);

  // Handle authentication failure from WebSocket
  const handleAuthFailed = useCallback(() => {
    debugLog('🚫 Authentication failed, clearing session');
    clearUserSession();
    setIsAuthenticated(false);
    setUserInfo(null);
    setUserEmail(null);
    setAuthCode(null);
  }, []);

  // Connect websocket (always connected for registration/login/authenticated users)
  const { status, data, furthestDistanceData, allTimeFurthestDistanceData, receiverStatistics, connectionEvents, logs, updatesPerSecond, airportActivityUpdate, acarsMessages, notamUpdates, predictionUpdate, departureForecastUpdate, connectionEpoch, sendMessage, mergeSnapshot, addLog } = useWebSocket(userEmail, authCode, handleAuthResponse, isAuthenticated, handleAuthFailed, selectedReceiverUuid);

  // Keep the 24h access token rotated ahead of expiry so the 7-day sliding
  // refresh window actually advances with activity — and so a truly dead
  // session (expired refresh token) redirects to login from EVERY page,
  // including the WebSocket-only Live map. See the hook for the full rationale.
  useProactiveTokenRefresh(isAuthenticated);

  // Fetch initial range outline data via HTTP API for faster loading
  // WebSocket will override this data once it starts pushing updates
  useEffect(() => {
    if (!isAuthenticated || !selectedReceiverUuid || selectedReceiverUuid === 'ALL_RECEIVERS') {
      setInitialFurthestData(null);
      setInitialAllTimeFurthestData(null);
      return;
    }

    const fetchInitialRangeData = async () => {
      const token = getAccessToken();
      if (!token) return;

      debugLog('📡 Fetching initial range outline data via HTTP...');

      try {
        // Fetch both daily and all-time data in parallel
        const [dailyResponse, allTimeResponse] = await Promise.all([
          getMaxDistancePointsHttp(selectedReceiverUuid, null, true, false, token),
          getMaxDistancePointsHttp(selectedReceiverUuid, null, true, true, token)
        ]);

        // Transform HTTP response to match WebSocket format:
        // { [receiverUuid]: { bearingRecords: [...], timestamp, receiverUuid } }
        if (dailyResponse.header?.apiStatus === 'OK' || dailyResponse.header?.apiStatus === 'SUCCESS') {
          const points = dailyResponse.points || dailyResponse.maxDistancePoints || [];
          debugLog('✅ Initial daily range data loaded:', points.length, 'points');
          // Format to match WebSocket structure
          const formattedData = {
            [selectedReceiverUuid]: {
              bearingRecords: points,
              timestamp: new Date().toISOString(),
              receiverUuid: selectedReceiverUuid
            }
          };
          debugLog('📍 Formatted daily data:', formattedData);
          setInitialFurthestData(formattedData);
        }

        if (allTimeResponse.header?.apiStatus === 'OK' || allTimeResponse.header?.apiStatus === 'SUCCESS') {
          const points = allTimeResponse.points || allTimeResponse.maxDistancePoints || [];
          debugLog('✅ Initial all-time range data loaded:', points.length, 'points');
          // Format to match WebSocket structure
          const formattedData = {
            [selectedReceiverUuid]: {
              bearingRecords: points,
              timestamp: new Date().toISOString(),
              receiverUuid: selectedReceiverUuid
            }
          };
          debugLog('📍 Formatted all-time data:', formattedData);
          setInitialAllTimeFurthestData(formattedData);
        }
      } catch (error) {
        debugError('❌ Error fetching initial range outline data:', error);
      }
    };

    fetchInitialRangeData();

    // The backend only PUSHES furthest-distance updates when a new record is set
    // (sparse), and never re-sends the current outline on connect — so with the
    // WebSocket briefly down, or between rare new-record events, the drawn daily
    // outline lags reality. Re-fetch the snapshots periodically so the outlines
    // stay current without depending on live WS frames (also re-tries the
    // all-time snapshot, which will appear as soon as the backend has records).
    const refreshId = setInterval(fetchInitialRangeData, 90000); // 90s
    return () => clearInterval(refreshId);
  }, [isAuthenticated, selectedReceiverUuid]);

  // One-time verified-state snapshot on receiver switch.
  //
  // The WebSocket only streams incremental aircraft_update messages, and the hook clears the
  // aircraft store on every receiver change — so aircraft that already hold a validated
  // position on the backend show blue (unverified) until their NEXT position frame streams
  // in. Long-range traffic with sparse fixes can sit blue for a long time even though the
  // backend has a valid latched position (and further, the backend suppresses broadcasting
  // any position older than 60s). Fetch the backend's current per-receiver validated snapshot
  // and merge it so already-verified aircraft render verified immediately. Skipped for All
  // Receivers, which already receives every receiver's stream and has no single snapshot.
  //
  // Keyed on connectionEpoch as well: after a backend restart the hook's 60s sweep has
  // emptied the store, and the reconnected stream recreates aircraft without positions
  // until each one's next fix - the table filled but the map stayed empty until a hard
  // refresh (which re-ran this effect). Re-fetching per connection is that refresh.
  useEffect(() => {
    if (!isAuthenticated || !selectedReceiverUuid || selectedReceiverUuid === 'ALL_RECEIVERS') {
      return undefined;
    }

    const receiverUuid = selectedReceiverUuid;
    let cancelled = false;

    const fetchSnapshot = async () => {
      const token = getAccessToken();
      if (!token) return;

      try {
        const response = await getReceiverSnapshotHttp(receiverUuid, token);
        if (cancelled) return;
        const apiStatus = response?.header?.apiStatus;
        if (apiStatus && apiStatus !== 'OK' && apiStatus !== 'SUCCESS') {
          debugWarn('⚠️ Receiver snapshot returned non-OK status:', apiStatus);
          return;
        }
        const aircraft = response?.aircraft || [];
        debugLog(`📸 Receiver snapshot for ${receiverUuid}: ${aircraft.length} aircraft`);
        mergeSnapshot(receiverUuid, aircraft);
      } catch (error) {
        // Non-fatal: the live WebSocket stream still fills aircraft in incrementally.
        debugError('❌ Error fetching receiver snapshot:', error);
      }
    };

    fetchSnapshot();

    return () => { cancelled = true; };
  }, [isAuthenticated, selectedReceiverUuid, mergeSnapshot, connectionEpoch]);

  // Merge the HTTP snapshot with WebSocket pushes PER RECEIVER (a push is that
  // receiver's full outline, so it wins for that receiver only). The backend pushes
  // for every receiver the user owns, so an all-or-nothing "WS wins if non-empty"
  // merge threw the selected receiver's snapshot away as soon as any OTHER receiver
  // set a record, and its outline vanished until it set a record of its own.
  const mergedFurthestDistanceData = useMemo(
    () => mergeRangeData(initialFurthestData, furthestDistanceData),
    [furthestDistanceData, initialFurthestData]
  );

  const mergedAllTimeFurthestDistanceData = useMemo(
    () => mergeRangeData(initialAllTimeFurthestData, allTimeFurthestDistanceData),
    [allTimeFurthestDistanceData, initialAllTimeFurthestData]
  );

  // Filter aircraft data based on current filter
  const filteredData = useMemo(() => {
    if (!data) return data;

    // Always filter out ground vehicles unless explicitly showing them — and never
    // when Settings → "Ground vehicles" is Hidden. Read directly (App renders the
    // SettingsProvider, so it cannot use the hook); data refreshes every few seconds
    // so the toggle takes effect on the next update.
    const showGroundVehicles = aircraftFilter === 'groundVehicles'
      && getColourSettings().showGroundVehicles !== false;

    if (aircraftFilter === 'all') {
      // Default: show all except ground vehicles
      const filtered = {};
      Object.entries(data).forEach(([hex, aircraft]) => {
        if (!isGroundVehicle(aircraft)) filtered[hex] = aircraft;
      });
      return filtered;
    }

    const filtered = {};
    Object.entries(data).forEach(([hex, aircraft]) => {
      const isGV = isGroundVehicle(aircraft);
      const isNew = !aircraft.last_seen_at && !isGV;
      const isMilitary = aircraft.military === true || isMilitaryAircraft(aircraft.category);

      switch (aircraftFilter) {
        case 'new':
          if (isNew) filtered[hex] = aircraft;
          break;
        case 'notRegular':
          if (isNew || isMilitary) filtered[hex] = aircraft;
          break;
        case 'military':
          if (isMilitary) filtered[hex] = aircraft;
          break;
        case 'groundVehicles':
          if (isGV && showGroundVehicles) filtered[hex] = aircraft;
          break;
        default:
          if (!isGV) filtered[hex] = aircraft;
      }
    });
    return filtered;
  }, [data, aircraftFilter]);

  // HUD counters. All of them count the SAME population — whatever the active
  // aircraft filter is showing, i.e. exactly the rows in the list — so the
  // strip adds up: Total (on the map) + No Pos = everything tracked, and New
  // and Mil are subsets of that.
  //
  // Mil deliberately does NOT require a position. It used to, which made it
  // disagree with the list every time a military aircraft went briefly
  // position-less: the list still showed 4 rows while the counter read 2.
  // New and Mil also used to count over the unfiltered `data`, so with a
  // filter on they described a different set of aircraft than Total did.
  const hudCounts = useMemo(() => {
    let onMap = 0, noPosition = 0, isNew = 0, military = 0;
    Object.values(filteredData || {}).forEach(ac => {
      if (ac.lat != null && ac.lon != null) onMap++; else noPosition++;
      if (!ac.last_seen_at && !isGroundVehicle(ac)) isNew++;
      if (ac.military === true || isMilitaryAircraft(ac.category)) military++;
    });
    return { onMap, noPosition, isNew, military };
  }, [filteredData]);

  // Max range for the HUD: "now" = furthest live aircraft (r_dst is the
  // receiver-relative distance the backend stamps on each aircraft), "today" =
  // furthest bearing record in the daily max-distance outline data.
  const currentMaxRangeKm = useMemo(() => {
    let max = 0;
    Object.values(filteredData || {}).forEach(ac => {
      if (typeof ac.r_dst === 'number' && ac.r_dst > max) max = ac.r_dst;
    });
    return max;
  }, [filteredData]);

  // Only the selected receiver's records — the merged map holds every receiver
  // the backend pushed for.
  const todayMaxRangeKm = useMemo(
    () => maxRangeKm(mergedFurthestDistanceData,
      selectedReceiverUuid === 'ALL_RECEIVERS' ? null : selectedReceiverUuid),
    [mergedFurthestDistanceData, selectedReceiverUuid]
  );

  // Handler for airport updates from Map component
  const handleAirportsUpdate = useCallback((airportsData) => {
    debugLog('✈️ Received airports update from Map component:', airportsData.length, 'airports');

    // Get airport display settings
    const airportSettings = getAirportSettings();
    const displayOption = AIRPORT_DISPLAY_OPTIONS[airportSettings.display];

    // If display is 'none', don't show any airports
    if (!displayOption || displayOption.types.length === 0) {
      debugLog('✈️ Airport display disabled');
      setAirports([]);
      return;
    }

    // Filter airports based on display setting
    const allowedTypes = displayOption.types;
    const airportsToDisplay = airportsData.filter(airport =>
      allowedTypes.includes(airport.type)
    );

    debugLog('✈️ Filtered airports:', airportsToDisplay.length, 'of', airportsData.length, '(display:', airportSettings.display, ')');
    setAirports(airportsToDisplay);
  }, []);

  // Note: Aircraft track points are now fetched via HTTP API in handleAircraftSelect
  // No longer using WebSocket trackPoints state

  // Clear track if selected aircraft expires/is removed from map
  useEffect(() => {
    if (selectedAircraft && data) {
      // Check if selected aircraft still exists in current aircraft data
      if (!data[selectedAircraft.hex]) {
        debugLog('✈️ Selected aircraft expired:', selectedAircraft.hex);
        addLog(`✈️ Aircraft ${selectedAircraft.flight || selectedAircraft.hex} expired - clearing track`);
        setSelectedAircraft(null);
        setAircraftTrack(null);
      }
    }
  }, [data, selectedAircraft, addLog]);

  // Update receiver isActive / status / lastActiveAt fields from the
  // periodic receiverStatistics broadcast. Mirrors the same merge logic
  // for both owned receivers and viewer-shared receivers — without the
  // viewer half, a viewer-only user sees the receiver permanently
  // "OFFLINE" because its initial state from getAccountInfo lacks status
  // and the broadcast updates only ever flowed into the owner-list.
  useEffect(() => {
    if (!receiverStatistics || !receiverStatistics.receivers) return;
    // The built-in Map constructor is shadowed by `import Map from './components/Map'`
    // at the top of this file, so look stats up via a plain object instead.
    const statsByUuid = {};
    for (const r of receiverStatistics.receivers) {
      if (r && r.receiverUuid) statsByUuid[r.receiverUuid] = r;
    }
    const mergeStats = (prev) => {
      if (!prev || prev.length === 0) return prev;
      let hasChanges = false;
      const updated = prev.map(receiver => {
        const stats = statsByUuid[receiver.receiverUuid];
        if (!stats) return receiver;
        hasChanges = true;
        return {
          ...receiver,
          isActive: stats.isActive,
          status: stats.status || (stats.isActive ? 'ACTIVE' : 'OFFLINE'),
          lastActiveAt: stats.lastActiveAt || receiver.lastActiveAt,
          beastMsgRate: stats.beastMsgRate,
          acarsMsgRate: stats.acarsMsgRate,
          otherMsgRate: stats.otherMsgRate
        };
      });
      return hasChanges ? updated : prev;
    };
    setReceivers(mergeStats);
    setViewerReceivers(mergeStats);
  }, [receiverStatistics]);

  // Validate selected receiver exists in the list of receivers visible to
  // this user (owned + viewer-shared). Previously this only consulted the
  // owner-list; for a viewer-only user the owned list is empty, so the
  // effect would reset their selection to "All Receivers" on every load
  // and the map would default-center over Ireland. Now we honour
  // viewerReceivers too — if the user has viewer access to one or more
  // receivers, auto-select the first one when they don't own any.
  useEffect(() => {
    if (allVisibleReceivers.length === 0) return; // Wait for any receivers to load

    if (allVisibleReceivers.length === 1 || !hadStoredReceiverRef.current) {
      // Auto-select the only visible receiver, or — when this browser has never
      // stored a choice — the first one added, so the map opens centred on a
      // receiver instead of an arbitrary saved view. Once a choice is stored
      // (including "All Receivers") the last selection wins.
      hadStoredReceiverRef.current = true;
      const { receiverName, receiverUuid } = firstAddedReceiver(receivers, viewerReceivers);
      setSelectedReceiver(receiverName);
      setSelectedReceiverUuid(receiverUuid);
      localStorage.setItem('selectedReceiver', receiverName);
      localStorage.setItem('selectedReceiverUuid', receiverUuid);
    } else if (selectedReceiver !== 'All Receivers'
        && !allVisibleReceivers.find(r => r.receiverName === selectedReceiver)) {
      // If selected receiver no longer exists in the visible list, reset.
      setSelectedReceiver('All Receivers');
      setSelectedReceiverUuid('ALL_RECEIVERS');
      localStorage.setItem('selectedReceiver', 'All Receivers');
      localStorage.setItem('selectedReceiverUuid', 'ALL_RECEIVERS');
    }
  }, [allVisibleReceivers, selectedReceiver, receivers, viewerReceivers]);

  const handleReceiverChange = useCallback((receiverName) => {
    setSelectedReceiver(receiverName);
    // Save to localStorage for persistence across page refreshes
    localStorage.setItem('selectedReceiver', receiverName);

    // Send selectReceiver message to backend
    const token = getAccessToken();
    if (!token) {
      debugWarn('⚠️ Cannot send selectReceiver - no access token');
      return;
    }

    let receiverUuid;
    if (receiverName === 'All Receivers') {
      receiverUuid = 'ALL_RECEIVERS';
      debugLog('📡 Sending selectReceiver: ALL_RECEIVERS');
    } else {
      // Find the receiver UUID by name in either owned receivers OR
      // viewer-shared receivers — without the viewer fallback a
      // receiver-viewer's selection couldn't be resolved and the
      // backend would never get a selectReceiver message.
      const receiver = allVisibleReceivers.find(r => r.receiverName === receiverName);
      if (receiver) {
        receiverUuid = receiver.receiverUuid;
        debugLog(`📡 Sending selectReceiver: ${receiverName} (${receiverUuid})`);
      } else {
        debugError(`❌ Cannot find receiver UUID for: ${receiverName}`);
        return;
      }
    }

    // Update selected receiver UUID state - this will trigger aircraft data clearing in useWebSocket
    setSelectedReceiverUuid(receiverUuid);
    // Save to localStorage for persistence across page refreshes
    localStorage.setItem('selectedReceiverUuid', receiverUuid);

    // Send WebSocket message to select receiver
    sendMessage('WebsocketEndpoint', 'selectReceiver', { receiverUuid }, token);
  }, [allVisibleReceivers, sendMessage]);

  // Handle pull-to-refresh on mobile.
  // Use forceReload() (unregister service worker + clear all caches) rather than
  // window.location.reload(): a soft reload is served straight back out of the
  // service-worker cache, so on iOS a stale bundle never updates. forceReload
  // tears down the SW + caches first, guaranteeing a fresh index.html + bundle —
  // this is the manual escape hatch when a device is stuck on an old build.
  const handlePullRefresh = useCallback(async () => {
    await forceReload();
  }, []);

  // Handle aircraft selection for track display
  const handleAircraftSelect = async (aircraft) => {
    // Bump the selection sequence on every action — including deselect/close.
    // Any in-flight response from a previous click that resolves later
    // will have a stale captured seq and be discarded.
    const mySeq = ++aircraftSelectSeqRef.current;

    if (!aircraft) {
      // Deselect aircraft - clear track
      setSelectedAircraft(null);
      setAircraftTrack(null);
      addLog('✈️ Aircraft deselected');
      return;
    }

    // If same aircraft clicked, deselect it
    if (selectedAircraft && selectedAircraft.hex === aircraft.hex) {
      setSelectedAircraft(null);
      setAircraftTrack(null);
      addLog('✈️ Aircraft deselected');
      return;
    }

    // Select new aircraft
    setSelectedAircraft(aircraft);
    setAircraftTrack(null); // Clear previous track

    // On a phone the table and the map are separate views, so selecting from
    // the table left the user on the list with the details popup — and the
    // track — rendered on a map they could not see. Bring the map forward.
    if (isMobileLayout && mobileView === 'table') {
      setMobileView('map');
    }

    // Log aircraft selection
    const callsign = aircraft.flight || aircraft.hex;
    addLog(`✈️ Aircraft selected: ${callsign} (${aircraft.hex})`);

    debugLog('✈️ Aircraft selected:', {
      hex: aircraft.hex,
      callsign: aircraft.flight,
      track_id: aircraft.track_id,
      trackId: aircraft.trackId,
      track_ID: aircraft.track_ID,
      hasAnyTrackId: !!(aircraft.track_id || aircraft.trackId || aircraft.track_ID),
      allFields: Object.keys(aircraft)
    });

    // Request track points if aircraft has trackId via HTTP API
    // Try multiple possible field names
    const trackId = aircraft.track_id || aircraft.trackId || aircraft.track_ID;

    if (trackId !== null && trackId !== undefined && trackId !== '') {
      debugLog('📍 Requesting track points (HTTP API) for aircraft:', aircraft.hex, 'trackId:', trackId);
      addLog(`📍 Requesting track for ${callsign} (trackId: ${trackId})`);

      try {
        const token = getAccessToken();
        const response = await getTrackPointsHttp(trackId, 5, token);

        // Stale-response guard: if the user clicked something else (or
        // closed the popup) while we were waiting on the network, drop
        // this response on the floor. Otherwise it would overwrite the
        // current selection's clean state with old aircraft data.
        if (mySeq !== aircraftSelectSeqRef.current) {
          debugLog('⏭️ Discarding stale track response for', aircraft.hex, '(seq mismatch)');
          return;
        }

        if (response.header?.apiStatus === 'SUCCESS' || response.header?.apiStatus === 'OK') {
          debugLog('✅ Track points received:', response.points?.length || 0, 'points');
          setAircraftTrack(response);
        } else {
          debugError('❌ Track request failed:', response.header?.errors);
          addLog(`❌ Failed to get track for ${callsign}`);
          setAircraftTrack(null);
        }
      } catch (error) {
        if (mySeq !== aircraftSelectSeqRef.current) return;
        debugError('❌ Error fetching track points:', error);
        addLog(`❌ Error fetching track for ${callsign}`);
        setAircraftTrack(null);
      }
    } else {
      addLog(`⚠️ No valid trackId for ${callsign}`);
      debugWarn('⚠️ Aircraft does not have a valid trackId:', aircraft.hex);
    }
  };

  const handleLogin = async (authResponse) => {
    // Handle HTTP authentication response
    // Response contains: accessToken, refreshToken, name, email, userId, expiryTimeUTC
    debugLog('🔍 Full authResponse received:', authResponse);

    const { accessToken, refreshToken, name, email, userId, username, expiryTimeUTC } = authResponse;

    debugLog('✓ HTTP Authentication successful:', { name: name || username, email, userId, expiryTimeUTC });
    debugLog('📧 Email extracted from response:', email);

    // Save session with JWT token and expiry time
    const userInfoToSave = { name: name || username, email, userId };
    debugLog('💾 Saving user info to localStorage:', userInfoToSave);
    saveUserSession(accessToken, refreshToken, userInfoToSave, expiryTimeUTC);

    // Verify it was saved
    const savedInfo = getUserInfo();
    debugLog('✓ Verified saved user info:', savedInfo);

    // Set authenticated state
    setIsAuthenticated(true);
    setUserInfo({ name: name || username, email, userId });

    // Clear any legacy state
    setUserEmail(null);
    setAuthCode(null);

    // Fetch account info via HTTP API after successful login
    try {
      debugLog('📡 Fetching account info via HTTP API...');
      const accountResponse = await getAccountInfo(email, accessToken);

      if (accountResponse.header?.apiStatus === 'SUCCESS' || accountResponse.header?.apiStatus === 'OK') {
        debugLog('✅ Account info received:', accountResponse);

        // Update profile and receivers from account info
        if (accountResponse.profile) {
          setProfile(accountResponse.profile);
          debugLog('✓ Profile state updated from HTTP response');
        }

        if (accountResponse.receivers) {
          setReceivers(accountResponse.receivers);
          debugLog('✓ Receivers state updated from HTTP response:', accountResponse.receivers.length, 'receivers');
        }

        // Update viewer receivers
        if (accountResponse.viewerReceivers) {
          setViewerReceivers(accountResponse.viewerReceivers);
          debugLog('✓ Viewer receivers updated from HTTP response:', accountResponse.viewerReceivers.length, 'viewers');
        }

        // Update viewer only flag
        setIsViewerOnly(accountResponse.isViewerOnly || false);
        debugLog('✓ Is viewer only:', accountResponse.isViewerOnly);

        // If user has no receivers and no viewer access, redirect to receivers page
        const hasReceivers = accountResponse.receivers && accountResponse.receivers.length > 0;
        const hasViewerAccess = accountResponse.viewerReceivers && accountResponse.viewerReceivers.length > 0;
        if (!hasReceivers && !hasViewerAccess) {
          debugLog('📡 No receivers found, redirecting to receivers page');
          setCurrentPage('receivers');
        }
      } else {
        debugError('❌ Failed to fetch account info:', accountResponse.header?.errors);
      }
    } catch (error) {
      debugError('❌ Error fetching account info after login:', error);
    }
  };

  const handleLogout = () => {
    clearUserSession();
    setIsAuthenticated(false);
    setUserInfo(null);
    setUserEmail(null);
    setAuthCode(null);
  };

  const handleRegister = async (email, name, captchaToken = null) => {
    try {
      const response = await sendRegistrationRequest({
        email,
        name,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        language: 'en'
      }, captchaToken);

      debugLog('Registration response:', response);

      // Explicitly check for OK status before proceeding
      const apiStatus = response?.header?.apiStatus;
      if (apiStatus === 'OK' || apiStatus === 'SUCCESS') {
        debugLog('Registration successful, verification code sent');
        // Store registration data for verification step - this triggers showing CodeVerification
        setRegistrationData({ email, name });
        return true;
      } else {
        // Extract error message from response
        const error = response?.header?.errors?.[0]?.message
          || response?.message
          || 'Registration failed. Please try again.';
        debugError('Registration failed with status:', apiStatus, 'Error:', error);
        throw new Error(error);
      }
    } catch (err) {
      debugError('Registration error:', err);
      throw err;
    }
  };

  const handleVerifyEmail = async (code) => {
    if (!registrationData) return;

    try {
      // Send email verification request to HTTP API (for new user registration)
      const formattedCode = code.substring(0, 3) + '-' + code.substring(3);
      const response = await sendEmailVerificationRequest(registrationData.email, formattedCode);

      if (response.header?.apiStatus === 'OK' && response.accessToken) {
        const { accessToken, refreshToken, name, email, userId, username, expiryTimeUTC } = response;

        // Save session with expiry time
        saveUserSession(accessToken, refreshToken, { name: name || username || registrationData.name, email, userId }, expiryTimeUTC);
        setIsAuthenticated(true);
        setUserInfo({ name: name || username || registrationData.name, email, userId });
        setRegistrationData(null);

        // Fetch account info via HTTP API after successful registration
        try {
          debugLog('📡 Fetching account info via HTTP API after registration...');
          const accountResponse = await getAccountInfo(email, accessToken);

          if (accountResponse.header?.apiStatus === 'SUCCESS' || accountResponse.header?.apiStatus === 'OK') {
            debugLog('✅ Account info received:', accountResponse);

            // Update profile and receivers from account info
            if (accountResponse.profile) {
              setProfile(accountResponse.profile);
              debugLog('✓ Profile state updated from HTTP response');
            }

            if (accountResponse.receivers) {
              setReceivers(accountResponse.receivers);
              debugLog('✓ Receivers state updated from HTTP response:', accountResponse.receivers.length, 'receivers');
            }

            // Update viewer receivers
            if (accountResponse.viewerReceivers) {
              setViewerReceivers(accountResponse.viewerReceivers);
              debugLog('✓ Viewer receivers updated from HTTP response:', accountResponse.viewerReceivers.length, 'viewers');
            }

            // Update viewer only flag
            setIsViewerOnly(accountResponse.isViewerOnly || false);

            // If user has no receivers and no viewer access (new registration), redirect to receivers page
            const hasReceivers = accountResponse.receivers && accountResponse.receivers.length > 0;
            const hasViewerAccess = accountResponse.viewerReceivers && accountResponse.viewerReceivers.length > 0;
            if (!hasReceivers && !hasViewerAccess) {
              debugLog('📡 No receivers found (new user), redirecting to receivers page');
              setCurrentPage('receivers');
            }
          } else {
            debugError('❌ Failed to fetch account info:', accountResponse.header?.errors);
            // Still redirect new users to receivers page even if account info fetch failed
            debugLog('📡 Redirecting new user to receivers page');
            setCurrentPage('receivers');
          }
        } catch (error) {
          debugError('❌ Error fetching account info after registration:', error);
          // Still redirect new users to receivers page even if there was an error
          debugLog('📡 Redirecting new user to receivers page');
          setCurrentPage('receivers');
        }
      } else {
        const error = response.header?.errors?.[0]?.message || 'Invalid verification code. Please try again.';
        throw new Error(error);
      }
    } catch (err) {
      debugError('Email verification error:', err);
      throw err;
    }
  };

  const handleUpdateProfile = async (changes) => {
    try {
      const token = getAccessToken();
      const response = await updateProfileHttp(changes, token);

      // Check for errors in response (even if apiStatus is 'OK')
      const hasErrors = response.header?.errors && response.header.errors.length > 0;

      if (hasErrors) {
        const errorMessages = response.header.errors.map(err =>
          err.message || err.code || 'Unknown error'
        );

        debugError('❌ Profile update failed:', response.header.errors);
        return { success: false, errors: errorMessages };
      }

      if (response.header?.apiStatus === 'SUCCESS' || response.header?.apiStatus === 'OK') {
        debugLog('✅ Profile updated successfully');
        // Update local profile state
        setProfile(prevProfile => ({ ...prevProfile, ...changes }));
        return { success: true, message: 'Profile updated successfully' };
      } else {
        debugError('❌ Profile update failed - unexpected status:', response.header?.apiStatus);
        return { success: false, errors: ['Unexpected response status: ' + response.header?.apiStatus] };
      }
    } catch (error) {
      debugError('❌ Error updating profile:', error);
      return { success: false, errors: [error.message || 'Network error. Please try again.'] };
    }
  };

  const handleAutoZoomChange = useCallback((mode) => {
    setAutoZoom(mode);
    localStorage.setItem('autoZoom', mode);
  }, []);

  const handleAddReceiver = async (data) => {
    try {
      const token = getAccessToken();
      const response = await addReceiverHttp(data, token);

      // Check for errors in response (even if apiStatus is 'OK')
      const hasErrors = response.header?.errors && response.header.errors.length > 0;

      if (hasErrors) {
        // Extract error messages and return them for display on the page
        const errorMessages = response.header.errors.map(err =>
          err.message || err.code || 'Unknown error'
        );

        debugError('❌ Add receiver failed:', response.header.errors);
        return { success: false, errors: errorMessages };
      }

      if (response.header?.apiStatus === 'SUCCESS' || response.header?.apiStatus === 'OK') {
        debugLog('✅ Receiver added successfully:', response);
        // Add new receiver to state
        // Backend returns the full receiver object with generated receiverUuid
        const newReceiver = response.receiverUuid ? { ...data, receiverUuid: response.receiverUuid } : response;
        setReceivers(prev => [...prev, newReceiver]);
        return { success: true, receiver: newReceiver };
      } else {
        debugError('❌ Add receiver failed - unexpected status:', response.header?.apiStatus);
        return { success: false, errors: ['Unexpected response status: ' + response.header?.apiStatus] };
      }
    } catch (error) {
      debugError('❌ Error adding receiver:', error);
      return { success: false, errors: [error.message || 'Network error. Please try again.'] };
    }
  };

  const handleUpdateReceiver = async (data) => {
    try {
      const token = getAccessToken();
      const response = await updateReceiverHttp(data, token);

      // Check for errors in response (even if apiStatus is 'OK')
      const hasErrors = response.header?.errors && response.header.errors.length > 0;

      if (hasErrors) {
        const errorMessages = response.header.errors.map(err =>
          err.message || err.code || 'Unknown error'
        );

        debugError('❌ Update receiver failed:', response.header.errors);
        return { success: false, errors: errorMessages };
      }

      if (response.header?.apiStatus === 'SUCCESS' || response.header?.apiStatus === 'OK') {
        debugLog('✅ Receiver updated successfully');
        // Update receiver in state
        setReceivers(prev => prev.map(r =>
          r.receiverUuid === data.receiverUuid ? { ...r, ...data } : r
        ));
        return { success: true, message: 'Receiver updated successfully' };
      } else {
        debugError('❌ Update receiver failed - unexpected status:', response.header?.apiStatus);
        return { success: false, errors: ['Unexpected response status: ' + response.header?.apiStatus] };
      }
    } catch (error) {
      debugError('❌ Error updating receiver:', error);
      return { success: false, errors: [error.message || 'Network error. Please try again.'] };
    }
  };

  const handleDeleteReceiver = async (receiverUuid) => {
    try {
      const token = getAccessToken();
      const response = await deleteReceiverHttp(receiverUuid, token);

      // Check for errors in response (even if apiStatus is 'OK')
      const hasErrors = response.header?.errors && response.header.errors.length > 0;

      if (hasErrors) {
        const errorMessages = response.header.errors.map(err =>
          err.message || err.code || 'Unknown error'
        );

        debugError('❌ Delete receiver failed:', response.header.errors);
        return { success: false, errors: errorMessages };
      }

      if (response.header?.apiStatus === 'SUCCESS' || response.header?.apiStatus === 'OK') {
        debugLog('✅ Receiver deleted successfully');
        // Remove receiver from state
        setReceivers(prev => prev.filter(r => r.receiverUuid !== receiverUuid));
        return { success: true, message: 'Receiver deleted successfully' };
      } else {
        debugError('❌ Delete receiver failed - unexpected status:', response.header?.apiStatus);
        return { success: false, errors: ['Unexpected response status: ' + response.header?.apiStatus] };
      }
    } catch (error) {
      debugError('❌ Error deleting receiver:', error);
      return { success: false, errors: [error.message || 'Network error. Please try again.'] };
    }
  };

  // In-app navigation for pages that link to other pages (banners, Admin →
  // All Receivers). `opts.scope` pre-selects the Receivers scope; `opts.tab`
  // pre-selects a Downloads tab. Clears any pending "← Back" like the nav does.
  const navigateTo = (page, opts = {}) => {
    setReturnToPage(null);
    if (page === 'receivers') setReceiversInitialScope(opts.scope || null);
    if (page === 'downloads') setDownloadsTab(opts.tab || null);
    setCurrentPage(page);
  };

  // "View Live" from the Receivers page: put the receiver on the live map.
  // For a receiver the user does not own (admin, All receivers scope) it is
  // injected into the map's receiver list, flagged so the Receivers page
  // can leave it out of "My receivers".
  const viewReceiverLive = (receiver, returnTo = 'receivers') => {
    const receiverUuid = receiver.receiverUuid;
    const receiverName = receiver.receiverName;
    setReceivers(prevReceivers => {
      if (prevReceivers.some(r => r.receiverUuid === receiverUuid)) return prevReceivers;
      return [...prevReceivers, {
        receiverUuid,
        receiverName,
        airportName: receiver.airportName,
        city: receiver.nearestCity,
        country: receiver.nearestCityCountry,
        latitude: receiver.latitude,
        longitude: receiver.longitude,
        isAdminView: true
      }];
    });
    setSelectedReceiver(receiverName);
    setSelectedReceiverUuid(receiverUuid);
    localStorage.setItem('selectedReceiver', receiverName);
    localStorage.setItem('selectedReceiverUuid', receiverUuid);
    const token = getAccessToken();
    if (token) {
      debugLog('📡 Selecting receiver for live view:', receiverName, receiverUuid);
      sendMessage('WebsocketEndpoint', 'selectReceiver', { receiverUuid }, token);
    }
    setReturnToPage(returnTo);
    setCurrentPage('map');
  };

  // Show loading screen while checking authentication
  if (isCheckingAuth) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        fontSize: '24px'
      }}>
        Connecting to AvioTrack...
      </div>
    );
  }

  // Show login/registration screens if not authenticated
  if (!isAuthenticated) {
    // Show email verification after registration
    if (registrationData) {
      return (
        <CodeVerification
          email={registrationData.email}
          onVerify={handleVerifyEmail}
          onBack={() => setRegistrationData(null)}
          title="Verify Your Email"
          message={<>A verification code has been sent to <strong>{registrationData.email}</strong></>}
        />
      );
    }

    // Show privacy policy
    if (showPrivacy) {
      return (
        <Privacy onBack={() => setShowPrivacy(false)} />
      );
    }

    // Show registration form
    if (showRegistration) {
      return (
        <Registration
          onRegister={handleRegister}
          onBack={() => setShowRegistration(false)}
        />
      );
    }

    // Show login form
    return (
      <Login
        onLogin={handleLogin}
        userEmail={userEmail}
        onShowRegister={() => setShowRegistration(true)}
        onShowPrivacy={() => setShowPrivacy(true)}
      />
    );
  }

  return (
    <SettingsProvider>
    <div className={`App ${isMobileLayout && headerHidden ? 'header-collapsed' : ''} ${isMobileLayout && isFullscreen ? 'fullscreen-mode' : ''} ${isTabletLayout && !isFullscreen ? 'has-side-rail' : ''}`}>
      {isTabletLayout && !isFullscreen && (
        <SideRail currentPage={currentPage} onPageChange={(p) => { setReturnToPage(null); setCurrentPage(p); }} isViewerOnly={isViewerOnly} />
      )}
      {/* App-wide, because a dead socket makes every page stale — and because the header chip
          that used to be the only indicator is hidden on phones and in fullscreen, which is
          where the live map is actually watched. */}
      <ConnectionBanner status={status} />
      <header className={`App-header ${isMobileLayout ? 'mobile' : ''} ${isMobileLayout && headerHidden ? 'hidden' : ''} ${isMobileLayout && isFullscreen ? 'hidden' : ''}`}>
        <div className="logo-container">
          <img src="/aviotrack_logo.png" alt="AvioTrack" className="app-logo" />
        </div>
        {/* Hide navigation on mobile - BottomNav provides same functionality */}
        {!isMobileLayout && (
          <Navigation currentPage={currentPage} onPageChange={(p) => { setReturnToPage(null); setCurrentPage(p); }} userRoles={profile?.roles || []} isViewerOnly={isViewerOnly} />
        )}
        <div className="header-right">
          {!isMobileLayout && <span className="user-email">{userInfo?.name || userInfo?.email || 'User'}</span>}
          {!isMobileLayout && <button onClick={handleLogout} className="logout-button">Logout</button>}
          <WebSocketStatus status={status} />
          {/* Phone-only: avatar button replaces the missing user-email +
              logout. Keeps the header at 44 px without losing the controls. */}
          {isMobileLayout && (
            <HeaderAvatarMenu
              userInfo={userInfo}
              onLogout={handleLogout}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </header>

      {currentPage === 'map' && (
        <>
          {returnToPage && (
            <button
              className="back-to-receivers"
              onClick={() => { const back = returnToPage; setReturnToPage(null); setCurrentPage(back); }}
              title="Return to the receiver list you came from"
            >
              ← Back to Receivers
            </button>
          )}
          {/* Receiver / zoom / filter controls all live in the map's top-right
              liquid-glass control stack now — no DashboardInfo bar and no
              mobile controls bar, maximising map area on every layout. */}
          <div className={`App-content ${isMobileLayout ? 'mobile-layout' : ''}`}>
            {/* Map View */}
            <div
              className="App-map"
              style={{
                position: 'relative',
                width: isMobileLayout ? '100%' : `${100 - tableWidth}%`,
                display: isMobileLayout && mobileView !== 'map' ? 'none' : 'flex'
              }}
            >
              <Map
                aircraftData={filteredData || {}}
                receivers={allVisibleReceivers}
                selectedReceiver={selectedReceiver}
                airports={airports}
                furthestDistanceData={mergedFurthestDistanceData}
                allTimeFurthestDistanceData={mergedAllTimeFurthestDistanceData}
                aircraftTrack={aircraftTrack}
                selectedAircraftHex={selectedAircraft?.hex || null}
                onAircraftSelect={handleAircraftSelect}
                onAirportsUpdate={handleAirportsUpdate}
                autoZoom={autoZoom}
                resizeTrigger={mapResizeTrigger}
                aircraftFilter={aircraftFilter}
                onAircraftFilterChange={setAircraftFilter}
                receiverOptions={allVisibleReceivers}
                showAllReceiversOption={allVisibleReceivers.length > 1}
                onReceiverChange={handleReceiverChange}
                onAutoZoomChange={handleAutoZoomChange}
                isFullscreen={isFullscreen}
                onToggleFullscreen={isMobileLayout ? toggleFullscreen : null}
                onShowList={isMobileLayout ? () => setMobileView('table') : null}
                listCount={hudCounts.onMap + hudCounts.noPosition}
              />

              {/* Stats Overlay — the SAME standard overlay on desktop and mobile,
                  anchored at the top of the map. Every counter is always rendered
                  (even at 0) so positions stay fixed. */}
              {(!isMobileLayout || mobileView === 'map') && (
                <StatsOverlay
                  aircraftWithPositionCount={hudCounts.onMap}
                  aircraftNoPositionCount={hudCounts.noPosition}
                  activeNewCount={hudCounts.isNew}
                  militaryCount={hudCounts.military}
                  updatesPerSecond={updatesPerSecond}
                  currentMaxRangeKm={currentMaxRangeKm}
                  todayMaxRangeKm={todayMaxRangeKm}
                  receiverStatistics={receiverStatistics}
                  selectedReceiver={selectedReceiver}
                  receivers={allVisibleReceivers}
                  variant="live"
                />
              )}

              {/* Fullscreen + list buttons moved into the map's top-right
                  control stack (rendered by Map.js). */}

              {/* Watermark Logo */}
              <div className="map-watermark">
                <img src="/aviotrack_logo.png" alt="" className="watermark-logo" />
              </div>
            </div>

            {/* Desktop: Resize Divider */}
            {!isMobileLayout && (
              <div
                className="resize-divider"
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                style={{
                  width: '8px',
                  cursor: 'ew-resize',
                  backgroundColor: isResizing ? '#FF6B35' : '#4a5568',
                  flexShrink: 0,
                  transition: isResizing ? 'none' : 'background-color 0.2s',
                  position: 'relative',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderLeft: '1px solid #2d3748',
                  borderRight: '1px solid #2d3748',
                  touchAction: 'none'
                }}
                onMouseEnter={(e) => {
                  if (!isResizing) e.currentTarget.style.backgroundColor = '#FF6B35';
                }}
                onMouseLeave={(e) => {
                  if (!isResizing) e.currentTarget.style.backgroundColor = '#4a5568';
                }}
              >
                <div style={{
                  width: '2px',
                  height: '40px',
                  background: 'repeating-linear-gradient(0deg, #718096 0px, #718096 4px, transparent 4px, transparent 8px)',
                  pointerEvents: 'none'
                }} />
              </div>
            )}

            {/* Table View */}
            <div
              className="App-table"
              style={{
                width: isMobileLayout ? '100%' : `${tableWidth}%`,
                display: isMobileLayout && mobileView !== 'table' ? 'none' : 'block',
                position: 'relative'
              }}
            >
              {isMobileLayout ? (
                // Mounted only while shown. Hiding it with display:none left
                // iOS with unpainted (blank) cards on the way back: see
                // .aircraft-card in DataTable.css. The sort lives here so it
                // survives the round trip through the map.
                mobileView === 'table' && (
                  <PullToRefresh onRefresh={handlePullRefresh}>
                    <DataTable
                      aircraftData={filteredData || {}}
                      updatesPerSecond={updatesPerSecond}
                      selectedAircraftHex={selectedAircraft?.hex || null}
                      onAircraftSelect={handleAircraftSelect}
                      sortConfig={liveTableSort}
                      onSortChange={setLiveTableSort}
                    />
                  </PullToRefresh>
                )
              ) : (
                <DataTable
                  aircraftData={filteredData || {}}
                  updatesPerSecond={updatesPerSecond}
                  selectedAircraftHex={selectedAircraft?.hex || null}
                  onAircraftSelect={handleAircraftSelect}
                />
              )}

              {/* Mobile: FAB to switch to map */}
              {isMobileLayout && mobileView === 'table' && (
                <button
                  className="list-fab-map"
                  onClick={() => setMobileView('map')}
                  onTouchEnd={(e) => { e.preventDefault(); setMobileView('map'); }}
                  aria-label="Show map"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
                    <line x1="8" y1="2" x2="8" y2="18"/>
                    <line x1="16" y1="6" x2="16" y2="22"/>
                  </svg>
                  <span>Map</span>
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {currentPage === 'account' && (
        <div className="App-content">
          <Account userInfo={userInfo} profile={profile} onUpdateProfile={handleUpdateProfile} />
        </div>
      )}

      {currentPage === 'receivers' && (
        <div className="App-content">
          <Receivers
            receivers={receivers.filter(r => !r.isAdminView)}
            receiverStatistics={receiverStatistics}
            onAddReceiver={handleAddReceiver}
            onUpdateReceiver={handleUpdateReceiver}
            onDeleteReceiver={handleDeleteReceiver}
            token={getAccessToken()}
            isAdmin={(profile?.roles || []).includes('ADMIN')}
            initialScope={receiversInitialScope}
            onInitialScopeConsumed={() => setReceiversInitialScope(null)}
            onViewLive={viewReceiverLive}
            onNavigate={navigateTo}
          />
        </div>
      )}

      {currentPage === 'history' && (
        <div className="App-content">
          <History
            receivers={allVisibleReceivers}
            selectedReceiverUuid={selectedReceiverUuid}
            onReceiverChange={handleReceiverChange}
            preselectHex={historyPreselectHex}
            preselectDate={historyPreselectDate}
            onPreselectConsumed={() => { setHistoryPreselectHex(null); setHistoryPreselectDate(null); }}
          />
        </div>
      )}

      {currentPage === 'predictions' && (
        <div className="App-content">
          <Predictions
            predictionUpdate={predictionUpdate}
            departureForecastUpdate={departureForecastUpdate}
            liveAircraft={data}
            selectedReceiverUuid={selectedReceiverUuid}
            onJumpToHistory={jumpToHistory}
          />
        </div>
      )}

      {currentPage === 'airport' && (
        <div className="App-content">
          <AirportBoard
            receivers={receivers}
            selectedReceiverUuid={selectedReceiverUuid}
            airportActivityUpdate={airportActivityUpdate}
            onJumpToHistory={jumpToHistory}
          />
        </div>
      )}

      {currentPage === 'messages' && (
        <div className="App-content">
          <Messages
            receivers={receivers}
            viewerReceivers={viewerReceivers}
            selectedReceiverUuid={selectedReceiverUuid}
            acarsMessages={acarsMessages}
            onReceiverChange={handleReceiverChange}
            aircraftData={data || {}}
          />
        </div>
      )}

      {currentPage === 'airband' && (
        <div className="App-content">
          <Airband
            receivers={receivers}
            viewerReceivers={viewerReceivers}
            selectedReceiverUuid={selectedReceiverUuid}
            onReceiverChange={handleReceiverChange}
          />
        </div>
      )}

      {currentPage === 'statistics' && (
        <div className="App-content">
          <Statistics
            receivers={receivers}
            viewerReceivers={viewerReceivers}
            selectedReceiverUuid={selectedReceiverUuid}
            onReceiverChange={handleReceiverChange}
            receiverStatistics={receiverStatistics}
          />
        </div>
      )}

      {currentPage === 'notams' && (
        <div className="App-content">
          <Notams notamUpdates={notamUpdates} userRoles={profile?.roles || []} />
        </div>
      )}

      {currentPage === 'settings' && (
        <div className="App-content">
          <Settings
            receivers={receivers}
            selectedReceiverUuid={selectedReceiverUuid}
          />
        </div>
      )}

      {currentPage === 'downloads' && (
        <div className="App-content">
          <Downloads initialTab={downloadsTab} onInitialTabConsumed={() => setDownloadsTab(null)} />
        </div>
      )}

      {currentPage === 'privacy' && (
        <div className="App-content">
          <Privacy />
        </div>
      )}

      {currentPage === 'admin' && (
        <div className="App-content">
          <Admin
            connectionEvents={connectionEvents}
            showDebugFooter={showDebugFooter}
            onToggleDebug={() => setShowDebugFooter(!showDebugFooter)}
            onNavigate={navigateTo}
            userRoles={profile?.roles || []}
            onViewLive={(receiver) => viewReceiverLive(receiver, 'admin')}
          />
        </div>
      )}

      {currentPage === 'geospatial' && (
        <div className="App-content">
          <GeospatialConfigurator />
        </div>
      )}

      {currentPage === 'billing' && (
        <div className="App-content">
          <BillingDashboard />
        </div>
      )}

      {/* Phone: bottom navigation. Tablet: side rail (rendered above as a
          flex sibling — see the rail-aware wrapper below). Both share the
          same nav-item config. Hidden in fullscreen mode. */}
      {isPhoneLayout && !isFullscreen && (
        <BottomNav currentPage={currentPage} onPageChange={setCurrentPage} isViewerOnly={isViewerOnly} />
      )}

      {/* Debug footer - controlled from Admin page */}
      {showDebugFooter && (
        <footer className="App-footer">
          <div className="debug-log">
            {logs.map((log, index) => (
              <div key={index}>{log}</div>
            ))}
          </div>
        </footer>
      )}
    </div>
    </SettingsProvider>
  );
}

// Top-level switch: /claim/<token> bypasses the main App so the magic-link
// recipient (who may not be logged in) hits a clean confirmation page.
function AppRoot() {
  if (isClaimRoute()) {
    return <ClaimReceiver />;
  }
  return <App />;
}

export default AppRoot;
